import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import { Clock, FolderOpen, Monitor, User, Terminal } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { LolBinActivityData } from "../interfaces/CommonInterfaces";
import { useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../ui/pagination";

export default function LolbinRecentActivity({
  activityData = [],
  loading,
  fromdetails = false,
}: {
  activityData: LolBinActivityData[];
  loading: boolean;
  fromdetails?: boolean;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(activityData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedActivity = activityData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getVisiblePages = () => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, "...", totalPages];
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const renderSkeletonItem = (index: number) => (
    <div
      key={`skeleton-${index}`}
      className="relative flex gap-4 animate-pulse"
    >
      <div className="flex flex-col items-center">
        <div className="w-12 h-3 mb-2 text-xs bg-gray-300 rounded" />
        <div className="w-8 h-8 bg-gray-300 rounded-full" />
        <div className="w-px h-16 mt-2 bg-gray-300"></div>
      </div>
      <div className="flex-1 pb-4 space-y-2">
        <div className="w-full h-4 bg-gray-300 rounded" />
        <div className="flex gap-2">
          <div className="w-24 h-3 bg-gray-300 rounded" />
          <div className="w-20 h-3 bg-gray-300 rounded" />
        </div>
        <div className="w-40 h-3 bg-gray-300 rounded" />
      </div>
    </div>
  );

  return (
    <Card className="overview-card analytics-lolbin-activity h-full">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Recent LOLBin Activity"
        description="Recorded process launches and their context"
        icon={Terminal}
        detail={
          !loading && activityData.length
            ? `${activityData.length.toLocaleString()} events`
            : undefined
        }
      />
      <CardContent className="overview-card-content">
        <div className="analytics-lolbin-feed space-y-4">
          {loading &&
            [...Array(itemsPerPage)].map((_, idx) => renderSkeletonItem(idx))}

          {!loading &&
            paginatedActivity.length === 0 &&
            (fromdetails ? (
              <OverviewEmptyState
                icon={Terminal}
                message="No LOLBin activity found."
              />
            ) : (
              <div className="py-12 text-sm text-center text-muted-foreground">
                No LOLBin activity found.
              </div>
            ))}

          {!loading &&
            paginatedActivity.map((activity, index) => (
              <div
                key={activity.id}
                className="analytics-lolbin-entry relative flex gap-4 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                data-testid={`lolbin-activity-${activity.id}`}
              >
                <div className="analytics-lolbin-marker flex flex-col items-center">
                  {!fromdetails && (
                    <div className="mb-2 text-xs text-muted-foreground whitespace-nowrap">
                      {activity.timeAgo}
                    </div>
                  )}
                  <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-primary">
                    {fromdetails ? (
                      <Terminal className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4 text-primary-foreground" />
                    )}
                  </div>
                  {!fromdetails && index < paginatedActivity.length - 1 && (
                    <div className="w-px h-16 mt-2 bg-border"></div>
                  )}
                </div>

                <div className="analytics-lolbin-copy flex-1 pb-4">
                  {fromdetails && (
                    <div className="overview-activity-topline">
                      <span className="overview-event-label">
                        Process launch
                      </span>
                      <span className="overview-event-time">
                        <Clock />
                        {activity.timeAgo}
                      </span>
                    </div>
                  )}
                  <div className="analytics-lolbin-description mb-2 text-xs font-medium leading-relaxed text-foreground">
                    {fromdetails ? (
                      <>
                        <p>{activity.processPath}</p>
                        <dl className="analytics-lolbin-process-meta">
                          <div>
                            <dt>Process ID</dt>
                            <dd>{activity.pid}</dd>
                          </div>
                          <div>
                            <dt>Launched process ID</dt>
                            <dd>{activity.launchedPid}</dd>
                          </div>
                        </dl>
                      </>
                    ) : (
                      <>
                        Process:{" "}
                        <span className="font-mono break-all">
                          {activity.processPath}
                        </span>{" "}
                        pid:{" "}
                        <span className="text-blue-600">{activity.pid}</span>{" "}
                        launched application:{" "}
                        <span className="font-mono break-all">
                          "{activity.launchedApp}"
                        </span>{" "}
                        with pid:{" "}
                        <span className="text-blue-600">
                          {activity.launchedPid}
                        </span>{" "}
                        for user:{" "}
                        <span className="text-foreground">{activity.user}</span>
                      </>
                    )}
                  </div>

                  <div className="analytics-lolbin-context space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Monitor className="flex-shrink-0 w-3 h-3" />
                      <span>{activity.computerName}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <User className="flex-shrink-0 w-3 h-3" />
                      <span>{activity.user}</span>
                    </div>
                    <div className="flex items-start gap-1 text-xs text-muted-foreground">
                      <FolderOpen className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span className="font-mono break-all text-wrap">
                        {activity.launchedApp}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {totalPages > 1 && !loading && (
          <div className="analytics-lolbin-pagination flex justify-center mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) setCurrentPage(currentPage - 1);
                    }}
                    className={
                      currentPage === 1 ? "pointer-events-none opacity-50" : ""
                    }
                  />
                </PaginationItem>

                {getVisiblePages().map((page, index) => (
                  <PaginationItem key={index}>
                    {page === "..." ? (
                      <span className="px-2 text-xs text-muted-foreground">
                        …
                      </span>
                    ) : (
                      <PaginationLink
                        href="#"
                        isActive={currentPage === page}
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(page as number);
                        }}
                        className={
                          currentPage === page
                            ? "bg-primary text-primary-foreground"
                            : ""
                        }
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages)
                        setCurrentPage(currentPage + 1);
                    }}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
